/**
 * Licences that verify offline — `docs/COMMERCIAL.md`.
 *
 * The app has no server and no account, so a licence cannot be something a
 * server remembers. It is a small file instead: who it was issued to, what
 * it covers, and an Ed25519 signature over exactly those bytes, made with a
 * private key that never leaves the seller. This build carries the matching
 * public key (`VITE_LICENSE_PUBLIC_KEY`) and checks the signature itself —
 * no network, no activation, no phoning home, and a licence file works on
 * every device the pilot copies it to, which is what a pilot with a Mac, a
 * PC and a phone expects.
 *
 * The model it supports is "perpetual, with updates for a period": a licence
 * names the last day of updates it covers, and the desktop updater compares
 * that with a new version's release date (`updateCovered`). Nothing stops
 * working when the period ends; newer versions simply are not offered.
 *
 * What this module deliberately does not do is gate anything. Which features,
 * if any, need a licence is a business decision, and until it is made every
 * build behaves the same with or without one.
 *
 * File format (`.fdvlicense`), JSON:
 *
 * ```json
 * { "payload": "<base64url of the UTF-8 JSON below>", "signature": "<base64url>" }
 * ```
 *
 * with the payload `LicensePayload`. The signature covers the payload's
 * bytes as encoded, so there is no canonical-JSON question to get wrong.
 */

export interface LicensePayload {
  /** Format version of the payload itself. */
  v: 1;
  /** The seller's own reference, for support. */
  id: string;
  name: string;
  email: string;
  /** e.g. `personal`, `instructor`, `club`. Opaque here. */
  edition: string;
  issuedAt: string;
  /** Last day (UTC, `YYYY-MM-DD`) of releases this licence covers, or `null` for all of them. */
  updatesUntil: string | null;
}

export interface LicenseFile {
  payload: string;
  signature: string;
}

export type LicenseCheck =
  | { ok: true; license: LicensePayload }
  | { ok: false; reason: 'malformed' | 'signature' | 'no-key' };

function fromBase64Url(text: string): Uint8Array<ArrayBuffer> {
  const base64 = text.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

export function toBase64Url(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function isPayload(value: unknown): value is LicensePayload {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const v = value as Record<string, unknown>;
  return (
    v.v === 1 &&
    typeof v.id === 'string' &&
    typeof v.name === 'string' &&
    typeof v.email === 'string' &&
    typeof v.edition === 'string' &&
    typeof v.issuedAt === 'string' &&
    (v.updatesUntil === null ||
      (typeof v.updatesUntil === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v.updatesUntil)))
  );
}

/** The public key this build was given, raw 32 bytes as base64url, or `undefined` for a build without one. */
export function buildPublicKey(): string | undefined {
  const key = import.meta.env.VITE_LICENSE_PUBLIC_KEY as string | undefined;
  return key === undefined || key.trim() === '' ? undefined : key.trim();
}

/**
 * Checks a licence file's signature and shape.
 *
 * @param publicKey  raw Ed25519 public key, base64url. Defaults to the build's.
 */
export async function verifyLicense(
  text: string,
  publicKey: string | undefined = buildPublicKey(),
): Promise<LicenseCheck> {
  if (publicKey === undefined) {
    return { ok: false, reason: 'no-key' };
  }
  let file: LicenseFile;
  let payloadBytes: Uint8Array<ArrayBuffer>;
  let payload: unknown;
  try {
    file = JSON.parse(text) as LicenseFile;
    payloadBytes = fromBase64Url(file.payload);
    payload = JSON.parse(new TextDecoder().decode(payloadBytes));
  } catch {
    return { ok: false, reason: 'malformed' };
  }
  if (!isPayload(payload) || typeof file.signature !== 'string') {
    return { ok: false, reason: 'malformed' };
  }
  try {
    const key = await crypto.subtle.importKey(
      'raw',
      fromBase64Url(publicKey),
      { name: 'Ed25519' },
      false,
      ['verify'],
    );
    const valid = await crypto.subtle.verify(
      { name: 'Ed25519' },
      key,
      fromBase64Url(file.signature),
      payloadBytes,
    );
    return valid ? { ok: true, license: payload } : { ok: false, reason: 'signature' };
  } catch {
    // A browser without Ed25519 in Web Crypto, or a key that is not one.
    return { ok: false, reason: 'signature' };
  }
}

/**
 * Whether a release is one this licence entitles the pilot to install.
 *
 * No licence, or a licence without an end date, covers everything: while
 * nothing is sold, updates are not withheld from anyone.
 *
 * @param releasedAt  the release's publication instant or date.
 */
export function updateCovered(license: LicensePayload | null, releasedAt: string): boolean {
  if (license?.updatesUntil == null) {
    return true;
  }
  return releasedAt.slice(0, 10) <= license.updatesUntil;
}
